import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { ChevronLeftIcon, ChevronRightIcon, SaveIcon, XIcon } from 'lucide-react';
import { WizardSteps } from './WizardSteps';
import { TrainingTypeStep } from './TrainingTypeStep';
import { LocationDateTimeStep } from './LocationDateTimeStep';
import { InstructorStep } from './InstructorStep';
import { ParticipantsStep } from './ParticipantsStep';
import { SummaryStep } from './SummaryStep';
import { SuccessStep } from './SuccessStep';
import { useNavigate } from 'react-router-dom';
// Define the training data structure
export interface TrainingData {
  id?: number;
  trainingTypeId: number | null;
  trainingTypeName?: string;
  locationId: number | null;
  locationName?: string;
  startDate: Date | null;
  endDate: Date | null;
  instructorId: number | null;
  instructorName?: string;
  participants: Participant[];
  notes: string;
  clientId: number | null;
  clientName?: string;
  status: 'draft' | 'scheduled' | 'completed' | 'cancelled';
}
export interface Participant {
  id: number;
  name: string;
  email: string;
  company?: string;
  attended?: boolean;
  notes?: string;
}
export function TrainingWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [trainingData, setTrainingData] = useState<TrainingData>({
    trainingTypeId: null,
    locationId: null,
    startDate: null,
    endDate: null,
    instructorId: null,
    participants: [],
    notes: '',
    clientId: null,
    status: 'draft'
  });
  // Steps in the wizard
  const steps = ['Trainingstype', 'Locatie & Tijd', 'Instructeur', 'Deelnemers', 'Samenvatting'];
  // Handle next step
  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };
  // Handle previous step
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  // Handle cancel
  const handleCancel = () => {
    if (window.confirm('Weet je zeker dat je wilt annuleren? Alle ingevoerde gegevens gaan verloren.')) {
      navigate('/trainings');
    }
  };
  // Handle submit
  const handleSubmit = () => {
    // In a real app, this would send the data to the server
    console.log('Training data submitted:', trainingData);
    // Move to success step
    setCurrentStep(steps.length);
  };
  // Update training data
  const updateTrainingData = (data: Partial<TrainingData>) => {
    setTrainingData(prev => ({
      ...prev,
      ...data
    }));
  };
  // Render current step content
  const renderStepContent = () => {
    if (currentStep === steps.length) {
      return <SuccessStep trainingData={trainingData} data-id="element-3441" />;
    }
    switch (currentStep) {
      case 0:
        return <TrainingTypeStep trainingData={trainingData} updateTrainingData={updateTrainingData} data-id="element-3442" />;
      case 1:
        return <LocationDateTimeStep trainingData={trainingData} updateTrainingData={updateTrainingData} data-id="element-3443" />;
      case 2:
        return <InstructorStep trainingData={trainingData} updateTrainingData={updateTrainingData} data-id="element-3444" />;
      case 3:
        return <ParticipantsStep trainingData={trainingData} updateTrainingData={updateTrainingData} data-id="element-3445" />;
      case 4:
        return <SummaryStep trainingData={trainingData} updateTrainingData={updateTrainingData} data-id="element-3446" />;
      default:
        return null;
    }
  };
  // Check if current step is valid and can proceed
  const canProceed = () => {
    switch (currentStep) {
      case 0:
        // Training Type
        return trainingData.trainingTypeId !== null;
      case 1:
        // Location & Date/Time
        return trainingData.locationId !== null && trainingData.startDate !== null && trainingData.endDate !== null;
      case 2:
        // Instructor
        return trainingData.instructorId !== null;
      case 3:
        // Participants
        return trainingData.participants.length > 0;
      case 4:
        // Summary
        return true;
      default:
        return false;
    }
  };
  return <div className="space-y-6" data-id="element-3447">
      <div className="flex justify-between items-center" data-id="element-3448">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-3449">Training Aanmaken</h1>
        <Button variant="outline" size="sm" onClick={handleCancel} data-id="element-3450">
          <XIcon className="h-4 w-4 mr-1" data-id="element-3451" />
          Annuleren
        </Button>
      </div>
      {currentStep <= steps.length - 1 && <WizardSteps steps={steps} currentStep={currentStep} onStepClick={step => {
      // Only allow going back to previous steps, not skipping ahead
      if (step < currentStep) {
        setCurrentStep(step);
      }
    }} data-id="element-3452" />}
      <Card className="overflow-hidden" data-id="element-3453">
        <div className="p-6" data-id="element-3454">{renderStepContent()}</div>
        {currentStep <= steps.length - 1 && <div className="flex justify-between items-center p-4 bg-gray-50 border-t border-gray-200" data-id="element-3455">
            <Button variant="outline" size="sm" onClick={handlePrevious} disabled={currentStep === 0} data-id="element-3456">
              <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-3457" />
              Vorige
            </Button>
            {currentStep < steps.length - 1 ? <Button variant="primary" size="sm" onClick={handleNext} disabled={!canProceed()} data-id="element-3458">
                Volgende
                <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-3459" />
              </Button> : <Button variant="primary" size="sm" onClick={handleSubmit} data-id="element-3460">
                <SaveIcon className="h-4 w-4 mr-1" data-id="element-3461" />
                Training Inplannen
              </Button>}
          </div>}
      </Card>
    </div>;
}